import type {RunOutcome} from './novaInvocationStore';

export type EligibilityReason = 'role' | 'task_locked' | 'no_agent_for_role' | 'no_genai_access';

// A disabled control can also be blocked by run capacity rather than the task's own eligibility —
// once three tasks are running at once, every other idle control disables itself until one
// finishes. 'run_cap_reached' widens the disabled reason to cover that alongside eligibility.
export type DisabledReason = EligibilityReason | 'run_cap_reached';

export type ControlState =
	| {kind: 'action'}
	| {kind: 'working'}
	| {kind: 'ready_or_continue'; read: boolean}
	| {kind: 'disabled'; reason: DisabledReason};
// Reserved, not rendered in v1 — see Design decisions above:
// | { kind: 'needs_input' } | { kind: 'actioned' } | { kind: 'failed' }

export function deriveControlState(input: {
	hasEverRun: boolean;
	isRunning: boolean;
	outputRead: boolean;
	eligibility: EligibilityReason | null;
	atRunCap?: boolean;
	// A failed or cancelled outcome is not a new visible row state — a failure is meant to be
	// legible wherever the conversation renders, not summarised on the task row itself. It
	// resolves the control back to plain Action here; the outcome itself stays on RunState for
	// whatever eventually renders that conversation view. In practice the store never produces
	// hasEverRun: true alongside a failed/cancelled outcome (see novaInvocationStore.invoke), but
	// this function's own contract checks outcome explicitly rather than relying on that as an
	// unstated invariant callers must maintain.
	outcome?: RunOutcome | null;
}): ControlState {
	if (input.eligibility) return {kind: 'disabled', reason: input.eligibility};
	if (input.isRunning) return {kind: 'working'};
	if (input.hasEverRun) {
		// The failed/cancelled override only applies here — a task that has genuinely never
		// succeeded falls through to the run-cap check below regardless of its outcome.
		if (input.outcome && (input.outcome.kind === 'failed' || input.outcome.kind === 'cancelled')) {
			return {kind: 'action'};
		}
		return {kind: 'ready_or_continue', read: input.outputRead};
	}
	if (input.atRunCap) return {kind: 'disabled', reason: 'run_cap_reached'};
	return {kind: 'action'};
}
